import React, { useMemo, useState } from 'react';
import {
  BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import { Repeat, UserPlus, Award, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { LineChart } from '../charts/LineChart';
import {
  useAccountHealthActivation,
  useAccountHealthRetention,
  useAccountHealthRewards
} from '../../hooks/useAdminDashboard';
import { fmtMoney, fmtPct, downloadCsv } from './accountHealthFormat';

// Trend sections of the Account Health tab: month-over-month revenue retention,
// signup → first order activation, and volume-rewards tier reach.

const UNASSIGNED = 'unassigned';

// Gains share one blue ramp and losses one red ramp (diverging poles), each
// stepped light → dark and validated as ordinal ramps against both surfaces.
const BRIDGE_COLORS = {
  light: { expansion: '#86b6ef', reactivated: '#3987e5', new: '#1c5cab', contraction: '#ee8f8e', churn: '#c93a3a' },
  dark: { expansion: '#1c5cab', reactivated: '#3987e5', new: '#86b6ef', contraction: '#b83333', churn: '#e87c7b' }
};
const RATIO_COLORS = {
  light: { nrr: '#2a78d6', grr: '#eb6834' },
  dark: { nrr: '#3987e5', grr: '#d95926' }
};

const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const Card: React.FC<{
  icon: React.ElementType;
  iconClass: string;
  title: string;
  subtitle: string;
  controls?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon: Icon, iconClass, title, subtitle, controls, children }) => (
  <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconClass}`} />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      {controls}
    </div>
    {children}
  </div>
);

const RangeToggle: React.FC<{ value: number; options: number[]; unit: string; onChange: (n: number) => void }> = ({
  value, options, unit, onChange
}) => (
  <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label={`Range in ${unit}`}>
    {options.map(o => (
      <button
        key={o}
        onClick={() => onChange(o)}
        aria-pressed={value === o}
        className={`px-3 py-1.5 text-sm transition-colors ${value === o ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
      >
        {o} {unit}
      </button>
    ))}
  </div>
);

const LoadingRows: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div className="p-6 space-y-2">
    {[...Array(rows)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
  </div>
);

const ErrorLine: React.FC<{ error: unknown; fallback: string }> = ({ error, fallback }) => (
  <div className="p-6 text-sm text-red-600">{(error as Error)?.message || fallback}</div>
);

interface BridgeMonth {
  month: string;
  startingRevenue: number;
  endingRevenue: number;
  newRevenue: number;
  reactivatedRevenue: number;
  expansion: number;
  contraction: number;
  churn: number;
  nrr: number | null;
  grr: number | null;
  accountsStart: number;
  retainedAccounts: number;
  churnedAccounts: number;
  newAccounts: number;
  reactivatedAccounts: number;
}

export const RetentionSection: React.FC = () => {
  const [months, setMonths] = useState(6);
  const query = useAccountHealthRetention(months);
  const { isDark } = useTheme();
  const series: BridgeMonth[] = query.data?.series || [];
  const colors = isDark ? BRIDGE_COLORS.dark : BRIDGE_COLORS.light;
  const ratioColors = isDark ? RATIO_COLORS.dark : RATIO_COLORS.light;

  const chartData = series.map(s => ({
    label: monthLabel(s.month),
    expansion: s.expansion,
    reactivated: s.reactivatedRevenue,
    new: s.newRevenue,
    contraction: s.contraction,
    churn: s.churn,
    raw: s
  }));
  const ratioData = series.map(s => ({
    label: monthLabel(s.month),
    nrr: s.nrr === null ? null : Math.round(s.nrr * 1000) / 10,
    grr: s.grr === null ? null : Math.round(s.grr * 1000) / 10
  }));

  const gridColor = isDark ? 'rgba(255, 255, 255, 0.06)' : '#f0f0f0';
  const tickColor = isDark ? '#71717a' : '#6b7280';
  const axisColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const compactMoney = (v: number) => {
    const abs = Math.abs(v);
    const sign = v < 0 ? '−' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}K`;
    return `${sign}$${abs}`;
  };

  const BridgeTooltip: React.FC<any> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const s: BridgeMonth = payload[0].payload.raw;
    const row = (label: string, value: number, color?: string) => (
      <div className="flex items-center justify-between gap-6">
        <span className="flex items-center gap-1.5 text-gray-600">
          {color && <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />}
          {label}
        </span>
        <span className="font-medium text-gray-900 tabular-nums">{value < 0 ? '−' : ''}{fmtMoney(Math.abs(value))}</span>
      </div>
    );
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-xs space-y-1 min-w-[220px]">
        <div className="font-semibold text-gray-900 mb-1">{monthLabel(s.month)}</div>
        {row('Starting revenue', s.startingRevenue)}
        {row('New', s.newRevenue, colors.new)}
        {row('Reactivated', s.reactivatedRevenue, colors.reactivated)}
        {row('Expansion', s.expansion, colors.expansion)}
        {row('Contraction', s.contraction, colors.contraction)}
        {row('Churn', s.churn, colors.churn)}
        <div className="border-t border-gray-100 pt-1">{row('Ending revenue', s.endingRevenue)}</div>
      </div>
    );
  };

  return (
    <Card
      icon={Repeat}
      iconClass="text-blue-500"
      title="Revenue Retention"
      subtitle="How each complete month's revenue changed from the month before"
      controls={<RangeToggle value={months} options={[6, 12]} unit="months" onChange={setMonths} />}
    >
      {query.isError ? (
        <ErrorLine error={query.error} fallback="Failed to load revenue retention" />
      ) : query.isLoading ? (
        <LoadingRows rows={6} />
      ) : series.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">No paid revenue yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Where revenue came from and went</h4>
              <p className="text-xs text-gray-500 mb-2">Gains above zero, losses below</p>
              <ResponsiveContainer width="100%" height={280}>
                <RechartsBarChart data={chartData} stackOffset="sign" margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: tickColor }} stroke={axisColor} />
                  <YAxis tick={{ fontSize: 12, fill: tickColor }} stroke={axisColor} tickFormatter={compactMoney} width={56} />
                  <ReferenceLine y={0} stroke={tickColor} />
                  <Tooltip content={<BridgeTooltip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="expansion" name="Expansion" stackId="bridge" fill={colors.expansion} maxBarSize={40} />
                  <Bar dataKey="reactivated" name="Reactivated" stackId="bridge" fill={colors.reactivated} maxBarSize={40} />
                  <Bar dataKey="new" name="New" stackId="bridge" fill={colors.new} maxBarSize={40} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="contraction" name="Contraction" stackId="bridge" fill={colors.contraction} maxBarSize={40} />
                  <Bar dataKey="churn" name="Churn" stackId="bridge" fill={colors.churn} maxBarSize={40} radius={[4, 4, 0, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Retention rates</h4>
              <p className="text-xs text-gray-500 mb-2">
                Net (NRR): this month's spend from last month's accounts ÷ last month's revenue. Gross (GRR) caps each account at last month.
              </p>
              <LineChart
                data={ratioData}
                xKey="label"
                height={280}
                lines={[
                  { dataKey: 'nrr', name: 'Net retention', color: ratioColors.nrr, strokeWidth: 2 },
                  { dataKey: 'grr', name: 'Gross retention', color: ratioColors.grr, strokeWidth: 2 }
                ]}
                formatXAxisLabel={(v: string) => v}
                formatYAxisLabel={(v: number) => `${v}%`}
                formatTooltip={(v: any, name: string) => [v === null ? '—' : `${v}%`, name]}
              />
            </div>
          </div>
          <div className="overflow-x-auto border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-2 text-left">Month</th>
                  <th className="px-3 py-2 text-right">Start</th>
                  <th className="px-3 py-2 text-right">New</th>
                  <th className="px-3 py-2 text-right">Reactivated</th>
                  <th className="px-3 py-2 text-right">Expansion</th>
                  <th className="px-3 py-2 text-right">Contraction</th>
                  <th className="px-3 py-2 text-right">Churn</th>
                  <th className="px-3 py-2 text-right">End</th>
                  <th className="px-3 py-2 text-right">NRR</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Accounts kept</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...series].reverse().map(s => (
                  <tr key={s.month} className="text-gray-700 tabular-nums">
                    <td className="px-3 py-2 text-left font-medium text-gray-900">{monthLabel(s.month)}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(s.startingRevenue)}</td>
                    <td className="px-3 py-2 text-right">+{fmtMoney(s.newRevenue)}</td>
                    <td className="px-3 py-2 text-right">+{fmtMoney(s.reactivatedRevenue)}</td>
                    <td className="px-3 py-2 text-right">+{fmtMoney(s.expansion)}</td>
                    <td className="px-3 py-2 text-right">−{fmtMoney(Math.abs(s.contraction))}</td>
                    <td className="px-3 py-2 text-right">−{fmtMoney(Math.abs(s.churn))}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">{fmtMoney(s.endingRevenue)}</td>
                    <td className="px-3 py-2 text-right">{s.nrr === null ? '—' : fmtPct(s.nrr)}</td>
                    <td className="px-3 py-2 text-right">
                      {s.retainedAccounts}/{s.accountsStart}
                      <span className="text-gray-400"> · +{s.newAccounts + s.reactivatedAccounts}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
};

interface Cohort {
  month: string;
  signups: number;
  approved: number;
  ordered: number;
  orderedWithin30Days: number;
  approvedNeverOrdered: number;
}

interface NeverOrdered {
  physicianId: string;
  name: string;
  clinicName: string;
  email: string;
  amId: string | null;
  amName: string | null;
  signedUpAt: string;
  daysSinceSignup: number;
}

export const ActivationSection: React.FC<{
  amFilter: string;
  amOptions: { value: string; label: string }[];
  onAmFilterChange: (value: string) => void;
}> = ({ amFilter, amOptions, onAmFilterChange }) => {
  const [months, setMonths] = useState(6);
  const [showAll, setShowAll] = useState(false);
  const query = useAccountHealthActivation(months);
  const cohorts: Cohort[] = query.data?.cohorts || [];
  const neverOrdered: NeverOrdered[] = query.data?.neverOrdered || [];

  const filtered = useMemo(
    () => neverOrdered.filter(p => !amFilter || (p.amId || UNASSIGNED) === amFilter),
    [neverOrdered, amFilter]
  );
  const visible = showAll ? filtered : filtered.slice(0, 10);
  const pct = (n: number, d: number) => (d > 0 ? fmtPct(n / d) : '—');

  const exportNeverOrdered = () => {
    downloadCsv(
      'approved-never-ordered.csv',
      ['Account', 'Clinic', 'Email', 'Account Manager', 'Signed up', 'Days since signup'],
      filtered.map(p => [
        p.name, p.clinicName, p.email, p.amName || 'Unassigned',
        new Date(p.signedUpAt).toLocaleDateString('en-US'), p.daysSinceSignup
      ])
    );
  };

  return (
    <Card
      icon={UserPlus}
      iconClass="text-emerald-500"
      title="Signup Activation"
      subtitle="Physicians by signup month, and whether they went on to place a paid order"
      controls={<RangeToggle value={months} options={[6, 12]} unit="months" onChange={setMonths} />}
    >
      {query.isError ? (
        <ErrorLine error={query.error} fallback="Failed to load signup activation" />
      ) : query.isLoading ? (
        <LoadingRows rows={6} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-2 text-left">Signup month</th>
                  <th className="px-3 py-2 text-right">Signups</th>
                  <th className="px-3 py-2 text-right">Approved</th>
                  <th className="px-3 py-2 text-right">Ordered</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Ordered ÷ approved</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Within 30 days</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Approved, never ordered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...cohorts].reverse().map(c => (
                  <tr key={c.month} className="text-gray-700 tabular-nums">
                    <td className="px-3 py-2 font-medium text-gray-900">{monthLabel(c.month)}</td>
                    <td className="px-3 py-2 text-right">{c.signups}</td>
                    <td className="px-3 py-2 text-right">{c.approved}</td>
                    <td className="px-3 py-2 text-right">{c.ordered}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">{pct(c.ordered, c.approved)}</td>
                    <td className="px-3 py-2 text-right">{pct(c.orderedWithin30Days, c.signups)}</td>
                    <td className="px-3 py-2 text-right">{c.approvedNeverOrdered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
            Recent months are still maturing — a clinic approved this week hasn't had time to order.
          </p>

          <div className="border-t border-gray-100">
            <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Approved, never ordered</h4>
                <p className="text-xs text-gray-500">{filtered.length} accounts · newest signups first</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={amFilter}
                  onChange={e => onAmFilterChange(e.target.value)}
                  aria-label="Account manager"
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All account managers</option>
                  {amOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button
                  onClick={exportNeverOrdered}
                  disabled={filtered.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
              </div>
            </div>
            {filtered.length === 0 ? (
              <div className="px-4 pb-4 text-sm text-gray-500">None in this range.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-y border-gray-200">
                      <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <th className="px-3 py-2 text-left">Account</th>
                        <th className="px-3 py-2 text-left">AM</th>
                        <th className="px-3 py-2 text-right">Signed up</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visible.map(p => (
                        <tr key={p.physicianId} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <Link to={`/users?q=${encodeURIComponent(p.email)}`} className="font-medium text-gray-900 hover:text-blue-600">
                              {p.clinicName || p.name}
                            </Link>
                            {p.clinicName && <div className="text-xs text-gray-500">{p.name}</div>}
                          </td>
                          <td className="px-3 py-2 text-gray-700">{p.amName || <span className="text-gray-400">Unassigned</span>}</td>
                          <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                            {new Date(p.signedUpAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            <span className="text-gray-400"> · {p.daysSinceSignup}d</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filtered.length > 10 && (
                  <button
                    onClick={() => setShowAll(s => !s)}
                    className="w-full px-4 py-2 text-sm text-blue-600 hover:bg-gray-50 border-t border-gray-100"
                  >
                    {showAll ? 'Show fewer' : `Show all ${filtered.length}`}
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

interface RewardWeek {
  weekStart: string;
  orderingPhysicians: number;
  earnedTier: number;
  reachPct: number | null;
  nearMiss: number;
  tierCounts: Record<string, number>;
  redeemed: number;
  redemptionPct: number | null;
  rewardAmount: number;
  volume: number;
  tierVolume: number;
}

export const RewardsSection: React.FC = () => {
  const [weeks, setWeeks] = useState(8);
  const query = useAccountHealthRewards(weeks);
  const data = query.data;
  const series: RewardWeek[] = data?.series || [];
  const tiers: { label: string; thresholdAmount: number }[] = data?.tiers || [];

  const totals = useMemo(() => {
    const ordering = series.reduce((n, w) => n + w.orderingPhysicians, 0);
    const earned = series.reduce((n, w) => n + w.earnedTier, 0);
    const redeemed = series.reduce((n, w) => n + w.redeemed, 0);
    const reward = series.reduce((n, w) => n + w.rewardAmount, 0);
    const tierVolume = series.reduce((n, w) => n + w.tierVolume, 0);
    return {
      reach: ordering > 0 ? earned / ordering : null,
      redemption: earned > 0 ? redeemed / earned : null,
      reward,
      rewardRate: tierVolume > 0 ? reward / tierVolume : null
    };
  }, [series]);

  const weekLabel = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

  const lowest = tiers.length ? tiers[0] : null;

  return (
    <Card
      icon={Award}
      iconClass="text-amber-500"
      title="Volume Rewards Reach"
      subtitle={
        data
          ? `${data.programActive ? 'Program active' : 'Program inactive'} · ${data.enrolledPhysicians} enrolled physicians · tiers earned on a week's paid volume, redeemed the following week`
          : 'Tiers earned on a week\'s paid volume, redeemed the following week'
      }
      controls={<RangeToggle value={weeks} options={[8, 12]} unit="weeks" onChange={setWeeks} />}
    >
      {query.isError ? (
        <ErrorLine error={query.error} fallback="Failed to load rewards engagement" />
      ) : query.isLoading ? (
        <LoadingRows rows={6} />
      ) : series.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">No reward weeks recorded in this range.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100">
            {[
              { label: 'Ordering physicians who earned a tier', value: totals.reach === null ? '—' : fmtPct(totals.reach, 1) },
              { label: 'Earned tiers actually redeemed', value: totals.redemption === null ? '—' : fmtPct(totals.redemption) },
              { label: 'Rewards paid out', value: fmtMoney(totals.reward) },
              { label: 'Reward cost on qualifying volume', value: totals.rewardRate === null ? '—' : fmtPct(totals.rewardRate, 1) }
            ].map(t => (
              <div key={t.label} className="bg-white p-4">
                <div className="text-xs text-gray-500">{t.label}</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{t.value}</div>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-2 text-left">Week of</th>
                  <th className="px-3 py-2 text-right">Ordering</th>
                  <th className="px-3 py-2 text-right">Earned tier</th>
                  {tiers.map(t => (
                    <th key={t.label} className="px-3 py-2 text-right whitespace-nowrap" title={`${fmtMoney(t.thresholdAmount)}+ per week`}>
                      {t.label}
                    </th>
                  ))}
                  <th
                    className="px-3 py-2 text-right whitespace-nowrap"
                    title={lowest ? `Within 25% below the ${lowest.label} threshold (${fmtMoney(lowest.thresholdAmount)})` : undefined}
                  >
                    Near miss
                  </th>
                  <th className="px-3 py-2 text-right">Redeemed</th>
                  <th className="px-3 py-2 text-right">Reward $</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...series].reverse().map(w => (
                  <tr key={w.weekStart} className="text-gray-700 tabular-nums">
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{weekLabel(w.weekStart)}</td>
                    <td className="px-3 py-2 text-right">{w.orderingPhysicians}</td>
                    <td className="px-3 py-2 text-right">
                      {w.earnedTier}
                      <span className="text-gray-400"> · {w.reachPct === null ? '—' : fmtPct(w.reachPct)}</span>
                    </td>
                    {tiers.map(t => <td key={t.label} className="px-3 py-2 text-right">{w.tierCounts[t.label] || 0}</td>)}
                    <td className="px-3 py-2 text-right">{w.nearMiss}</td>
                    <td className="px-3 py-2 text-right">
                      {w.redeemed}
                      <span className="text-gray-400"> · {w.redemptionPct === null ? '—' : fmtPct(w.redemptionPct)}</span>
                    </td>
                    <td className="px-3 py-2 text-right">{fmtMoney(w.rewardAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lowest && (
            <p className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
              Near miss = no tier, but within 25% of the {lowest.label} threshold ({fmtMoney(lowest.thresholdAmount)}/week). The latest week's redemptions are still coming in, since tiers are redeemed during the week after they're earned.
            </p>
          )}
        </>
      )}
    </Card>
  );
};
